// PWA Install Prompt Component
// Shows a modal prompting users to install the app

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Download, Smartphone, Share, Plus, CheckCircle } from 'lucide-react';
import { useInstallPrompt } from '../hooks/useInstallPrompt';

interface InstallPromptProps {
  forceShow?: boolean;
  onClose?: () => void;
  variant?: 'modal' | 'banner' | 'inline';
}

export function InstallPrompt({
  forceShow = false,
  onClose,
  variant = 'modal',
}: InstallPromptProps) {
  const { t } = useTranslation();
  const {
    canInstall,
    isInstalled,
    isIOS,
    isStandalone,
    promptInstall,
    dismissPrompt,
    shouldShowPrompt,
    incrementVisitCount,
  } = useInstallPrompt();

  const [isVisible, setIsVisible] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [installSuccess, setInstallSuccess] = useState(false);

  // Increment visit count on mount
  useEffect(() => {
    incrementVisitCount();
  }, [incrementVisitCount]);

  // Determine if prompt should be shown
  useEffect(() => {
    if (forceShow) {
      setIsVisible(true);
    } else if (shouldShowPrompt()) {
      // Delay showing the prompt slightly for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [forceShow, shouldShowPrompt]);

  // Handle install button click
  const handleInstall = async () => {
    if (isIOS) {
      // iOS doesn't support the install prompt, show instructions
      return;
    }

    setIsInstalling(true);
    const result = await promptInstall();

    if (result === 'accepted') {
      setInstallSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 2000);
    }
    setIsInstalling(false);
  };

  // Handle close
  const handleClose = () => {
    setIsVisible(false);
    dismissPrompt();
    onClose?.();
  };

  // Don't render if already installed or not visible
  if (isInstalled || isStandalone || !isVisible) {
    return null;
  }

  // Don't render if can't install and not iOS (and not forced)
  if (!canInstall && !isIOS && !forceShow) {
    return null;
  }

  // Banner variant
  if (variant === 'banner') {
    return (
      <div className="fixed bottom-0 inset-x-0 z-50 p-4 bg-gradient-to-r from-cyan-600 to-cyan-500 text-white shadow-lg">
        <div className="max-w-screen-xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Download className="w-5 h-5" />
            </div>
            <div>
              <p className="font-medium">{t('install_prompt.title')}</p>
              <p className="text-sm text-cyan-100">
                {t('install_prompt.subtitle')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleClose}
              className="px-3 py-1.5 text-sm text-cyan-100 hover:text-white transition-colors"
            >
              {t('install_prompt.later')}
            </button>
            <button
              onClick={handleInstall}
              disabled={isInstalling}
              className="px-4 py-1.5 bg-white text-cyan-600 font-medium rounded-lg hover:bg-cyan-50 transition-colors disabled:opacity-50"
            >
              {isInstalling ? t('install_prompt.installing') : t('install_prompt.install')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Inline variant (for settings page)
  if (variant === 'inline') {
    if (installSuccess) {
      return (
        <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
          <CheckCircle className="w-6 h-6 text-green-600" />
          <div>
            <p className="font-medium text-green-900">{t('install_prompt.success_title')}</p>
            <p className="text-sm text-green-700">
              {t('install_prompt.success_subtitle')}
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="p-4 bg-gradient-to-br from-cyan-50 to-blue-50 border border-cyan-200 rounded-xl">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-cyan-100 rounded-xl">
            <Smartphone className="w-6 h-6 text-cyan-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">{t('install_prompt.inline_title')}</h3>
            <p className="mt-1 text-sm text-gray-600">
              {t('install_prompt.inline_subtitle')}
            </p>

            {isIOS ? (
              <div className="mt-3 p-3 bg-white rounded-lg border border-gray-200">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  {t('install_prompt.ios_inline_intro')}
                </p>
                <ol className="text-sm text-gray-600 space-y-1.5">
                  <li className="flex items-center gap-2">
                    <Share className="w-4 h-4 text-cyan-600" />
                    <span>{t('install_prompt.ios_share_label')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Plus className="w-4 h-4 text-cyan-600" />
                    <span>{t('install_prompt.ios_add_home')}</span>
                  </li>
                </ol>
              </div>
            ) : (
              <button
                onClick={handleInstall}
                disabled={isInstalling || !canInstall}
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white font-medium rounded-lg hover:bg-cyan-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="w-4 h-4" />
                {isInstalling ? t('install_prompt.installing') : t('install_prompt.install_now')}
              </button>
            )}

            {!canInstall && !isIOS && (
              <p className="mt-2 text-xs text-gray-500">
                {t('install_prompt.browser_unsupported')}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Modal variant (default)
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Success state */}
        {installSuccess ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">{t('install_prompt.modal_success_title')}</h2>
            <p className="mt-2 text-gray-600">
              {t('install_prompt.success_subtitle')}
            </p>
          </div>
        ) : (
          <>
            {/* Header with gradient */}
            <div className="px-6 pt-8 pb-6 bg-gradient-to-br from-cyan-500 to-blue-600 text-white text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-white rounded-2xl shadow-lg flex items-center justify-center">
                <img
                  src="/pwa-192x192.png"
                  alt="TrackFleet"
                  className="w-12 h-12 rounded-xl"
                />
              </div>
              <h2 className="text-xl font-bold">{t('install_prompt.title')}</h2>
              <p className="mt-1 text-cyan-100 text-sm">
                {t('install_prompt.modal_app_subtitle')}
              </p>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="space-y-3">
                <Feature
                  icon={<Smartphone className="w-5 h-5" />}
                  title={t('install_prompt.feature_quick_title')}
                  description={t('install_prompt.feature_quick_desc')}
                />
                <Feature
                  icon={<Download className="w-5 h-5" />}
                  title={t('install_prompt.feature_offline_title')}
                  description={t('install_prompt.feature_offline_desc')}
                />
              </div>

              {isIOS ? (
                <div className="mt-6 p-4 bg-gray-50 rounded-xl">
                  <p className="text-sm font-medium text-gray-700 mb-3">
                    {t('install_prompt.ios_modal_intro')}
                  </p>
                  <ol className="text-sm text-gray-600 space-y-2">
                    <li className="flex items-center gap-3">
                      <span className="w-6 h-6 flex items-center justify-center bg-cyan-100 text-cyan-600 rounded-full text-xs font-bold">
                        1
                      </span>
                      <span className="flex items-center gap-1">
                        {t('install_prompt.ios_modal_step_1_before')} <Share className="w-4 h-4 text-cyan-600" /> {t('install_prompt.ios_modal_step_1_button')}
                      </span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="w-6 h-6 flex items-center justify-center bg-cyan-100 text-cyan-600 rounded-full text-xs font-bold">
                        2
                      </span>
                      <span className="flex items-center gap-1">
                        {t('install_prompt.ios_modal_step_2_before')} <Plus className="w-4 h-4 text-cyan-600" /> {t('install_prompt.ios_modal_step_2_button')}
                      </span>
                    </li>
                  </ol>
                </div>
              ) : (
                <div className="mt-6 flex gap-3">
                  <button
                    onClick={handleClose}
                    className="flex-1 px-4 py-2.5 text-gray-700 font-medium border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    {t('install_prompt.later')}
                  </button>
                  <button
                    onClick={handleInstall}
                    disabled={isInstalling || !canInstall}
                    className="flex-1 px-4 py-2.5 bg-cyan-600 text-white font-medium rounded-xl hover:bg-cyan-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isInstalling ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        {t('install_prompt.installing')}
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4" />
                        {t('install_prompt.install')}
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Feature item component
function Feature({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="p-2 bg-cyan-50 text-cyan-600 rounded-lg">{icon}</div>
      <div>
        <p className="font-medium text-gray-900">{title}</p>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
    </div>
  );
}

export default InstallPrompt;
